import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { toaster } from '@/components/ui/toaster';
import { useAffiliateCsvImportBatchQuery } from '@/hooks/queries/use-affiliate-csv-import-job';
import { isTerminalImportStatus } from '@/stores/affiliate-csv-import-store';
import { STEP_VALUES, type WizardStep } from '../_hooks/useAffiliateImportWizard';

interface AffiliateImportBackgroundMonitorProps {
  programId: string;
  batchId: string;
  enabled: boolean;
  onOpenResults: () => void;
}

/** Announcing once per batch must survive a cold load, so the marker outlives the tab's memory. */
const announcedStorageKey = (batchId: string) => `affiliate-import-announced:${batchId}`;

const wasAnnounced = (batchId: string): boolean => {
  try {
    return window.sessionStorage.getItem(announcedStorageKey(batchId)) !== null;
  } catch {
    return false;
  }
};

const markAnnounced = (batchId: string): void => {
  try {
    window.sessionStorage.setItem(announcedStorageKey(batchId), '1');
  } catch {
    // Private-mode storage denial only costs a repeated toast; never block the announcement.
  }
};

/**
 * Keeps an import observable after its modal is dismissed. The durable job id
 * stays in the URL and this REST poller (rather than Pusher alone) announces a
 * terminal result with a direct route back to its report.
 */
const AffiliateImportBackgroundMonitor: React.FC<AffiliateImportBackgroundMonitorProps> = ({
  programId,
  batchId,
  enabled,
  onOpenResults,
}) => {
  const { t } = useTranslation();
  const announcedBatchId = useRef<string | null>(null);
  const batchQuery = useAffiliateCsvImportBatchQuery({ programId, batchId, enabled });

  useEffect(() => {
    if (!enabled || !batchQuery.data || batchQuery.data.notFound) return;

    const { progress } = batchQuery.data;
    if (
      !isTerminalImportStatus(progress.status) ||
      announcedBatchId.current === progress.batchId ||
      wasAnnounced(progress.batchId)
    ) {
      return;
    }

    announcedBatchId.current = progress.batchId;
    markAnnounced(progress.batchId);
    const didFail = progress.status === 'error';
    // A cancellation is the brand's own decision: announce it neutrally and drop
    // the "view results" CTA, which would read as an invitation to resume.
    const wasCancelled = progress.status === 'cancelled';
    toaster.create({
      id: `affiliate-import-complete-${progress.batchId}`,
      title: t(
        wasCancelled
          ? 'affiliateImport.background.cancelledTitle'
          : didFail
            ? 'affiliateImport.background.errorTitle'
            : 'affiliateImport.background.completedTitle'
      ),
      description: t(
        wasCancelled
          ? 'affiliateImport.background.cancelledDescription'
          : didFail
            ? 'affiliateImport.background.errorDescription'
            : 'affiliateImport.background.completedDescription'
      ),
      type: wasCancelled ? 'info' : didFail ? 'error' : 'success',
      duration: 15_000,
      ...(wasCancelled
        ? {}
        : {
            action: {
              label: t('affiliateImport.background.viewResults'),
              onClick: onOpenResults,
            },
          }),
    });
  }, [batchQuery.data, enabled, onOpenResults, t]);

  return null;
};

/** The affiliates list is the only route mounting the import modal. */
export const AFFILIATE_IMPORT_RESULTS_PATH = '/affiliates';

/** Mounted in the authenticated application shell, so route changes do not stop monitoring. */
export const AffiliateImportBackgroundMonitorRoot: React.FC = () => {
  const navigate = useNavigate();
  const [step] = useQueryState('step', parseAsStringEnum<WizardStep>(STEP_VALUES));
  const [programId] = useQueryState('program', parseAsString.withDefault(''));
  const [batchId] = useQueryState('job', parseAsString.withDefault(''));

  // Setting `step` alone only opens the modal on the route that mounts it. Navigate
  // instead, carrying the three keys atomically so the report never loses its context.
  const handleOpenResults = useCallback(() => {
    const search = new URLSearchParams({ step: 'done', program: programId, job: batchId });
    void navigate(`${AFFILIATE_IMPORT_RESULTS_PATH}?${search.toString()}`);
  }, [batchId, navigate, programId]);

  return (
    <AffiliateImportBackgroundMonitor
      programId={programId}
      batchId={batchId}
      enabled={step === null && Boolean(programId) && Boolean(batchId)}
      onOpenResults={handleOpenResults}
    />
  );
};

export default AffiliateImportBackgroundMonitor;
